import {
  Alert,
  AlertProps,
  Badge,
  BadgeProps,
  ButtonProps,
  ContextMenuDropdown,
  InputGroup,
  SkeletonLoader,
  TDropdownItem,
  H1,
  Prose,
} from '@payaca/ambrosia';
import { FC, PropsWithChildren, ReactNode } from 'react';

export interface IProps {
  title?: string;
  badge?: PropsWithChildren<BadgeProps>;
  firstAction?: PropsWithChildren<ButtonProps>;
  actions?: TDropdownItem[];
  /**
   * The opening segment of the action group, ahead of `firstAction`. Must render
   * an `InputGroup` child.
   */
  leadingAction?: ReactNode;
  alerts?: PropsWithChildren<AlertProps>[];
}

const PreviewDocument: FC<PropsWithChildren<IProps>> = (props) => {
  const {
    title,
    badge,
    firstAction,
    actions = [],
    leadingAction,
    alerts = [],
    children,
  } = props;

  return (
    <div className="flex">
      <Prose className="mx-auto w-full max-w-screen-xl">
        {/*Alerts*/}
        {!!alerts.length && (
          <div className="mb-4">
            {alerts.map((alert, index) => (
              <Alert key={index} {...alert} />
            ))}
          </div>
        )}

        {/*Header*/}
        <div className="mb-4 flex items-center justify-between gap-4">
          <H1>{title}</H1>

          {badge && <Badge {...badge} />}

          <div className="ml-auto">
            <InputGroup className="bg-white" sizeVariant="md">
              {leadingAction}

              {firstAction && (
                <InputGroup.Button variant={'secondary'} {...firstAction} />
              )}

              {actions.length > 0 && (
                <ContextMenuDropdown
                  as={InputGroup.Button}
                  variant={'secondary'}
                  menuItems={actions}
                />
              )}
            </InputGroup>
          </div>
        </div>

        {/*Main Context*/}
        {children}
      </Prose>
    </div>
  );
};

export const PreviewDocumentSkeleton: FC<PropsWithChildren> = ({
  children,
}) => {
  return (
    <div className="flex">
      <div className="mx-auto w-full max-w-screen-xl">
        {/*Header*/}
        <div className="mb-4 flex items-center justify-between gap-4">
          <SkeletonLoader.Title className="h-8 w-1/4" />
          <SkeletonLoader.Badge />
          <SkeletonLoader.Button className="w-20 ml-auto" />
        </div>

        {/*Main Context*/}
        {children}
      </div>
    </div>
  );
};

export default PreviewDocument;
